import { Box } from "@mui/material";
import { useGlobals } from "../../hooks/useGlobals";
import { useState } from "react";
import { MemberUpdateInput } from "../../../lib/types/member";
import { T } from "../../../lib/types/common";
import { sweetErrorHandling, sweetTopSmallSuccessAlert } from "../../../lib/sweetAlert";
import { Messages, serverApi } from "../../../lib/config";
import MemberService from "../../services/MemberService";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhoneIcon from "@mui/icons-material/Phone";

const DESCRIPTION_MAX_LENGTH = 100;

export interface SettingsProps {
  onClose?: () => void;
  onSaveSuccess?: () => void;
}

export function Settings({ onClose, onSaveSuccess }: SettingsProps) {
  const { authMember, setAuthMember } = useGlobals();
  const [memberImage, setMemberImage] = useState<string>(
    authMember?.memberImage
      ? `${serverApi}/${authMember.memberImage}`
      : "/icons/default-user.svg"
  );

  const [memberUpdateInput, setMemberUpdateInput] = useState<MemberUpdateInput>({
    memberNick: authMember?.memberNick,
    memberPhone: authMember?.memberPhone,
    memberAdress: authMember?.memberAdress,
    memberDesc: authMember?.memberDesc,
    memberImage: authMember?.memberImage,
  });

  const memberNickHandler = (e: T) => {
    memberUpdateInput.memberNick = e.target.value;
    setMemberUpdateInput({ ...memberUpdateInput });
  };

  const memberPhoneHandler = (e: T) => {
    memberUpdateInput.memberPhone = e.target.value;
    setMemberUpdateInput({ ...memberUpdateInput });
  };

  const memberAdressHandler = (e: T) => {
    memberUpdateInput.memberAdress = e.target.value;
    setMemberUpdateInput({ ...memberUpdateInput });
  };

  const memberDescriptionHandler = (e: T) => {
    const val = e.target.value.slice(0, DESCRIPTION_MAX_LENGTH);
    memberUpdateInput.memberDesc = val;
    setMemberUpdateInput({ ...memberUpdateInput });
  };

  const handleSubmitButton = async () => {
    try {
      if (!authMember) throw new Error(Messages.error2);
      if (
        memberUpdateInput.memberNick === "" ||
        memberUpdateInput.memberPhone === "" ||
        memberUpdateInput.memberAdress === "" ||
        (memberUpdateInput.memberDesc ?? "") === ""
      ) {
        throw new Error(Messages.error3);
      }

      const member = new MemberService();
      const result = await member.updateMember(memberUpdateInput);
      setAuthMember(result);

      await sweetTopSmallSuccessAlert("Modified successfully!", 700);
      onSaveSuccess?.();
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  const handleimageViewer = (e: T) => {
    const file = e.target.files[0];
    const fileType = file?.type;
    const validateImageTypes = ["image/jpg", "image/jpeg", "image/png"];

    if (file && fileType && validateImageTypes.includes(fileType)) {
      memberUpdateInput.memberImage = file;
      setMemberUpdateInput({ ...memberUpdateInput });
      setMemberImage(URL.createObjectURL(file));
    } else if (file) {
      sweetErrorHandling(Messages.error5).then();
    }
  };

  const descLength = (memberUpdateInput.memberDesc ?? "").length;

  return (
    <Box className="settings settings-modal-form">
      <p className="settings-modal-subtitle">
        Update your personal information and profile picture.
      </p>

      <div className="settings-avatar-wrap">
        <img src={memberImage} alt="" className="settings-avatar-img" />
        <label className="settings-avatar-camera" aria-label="Change photo">
          <CameraAltIcon />
          <input type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleimageViewer} />
        </label>
      </div>

      <div className="settings-field">
        <label className="settings-label">NAME</label>
        <input
          className="settings-input"
          type="text"
          placeholder="Your full name"
          value={memberUpdateInput.memberNick ?? ""}
          onChange={memberNickHandler}
          name="memberNick"
        />
      </div>

      <div className="settings-field">
        <label className="settings-label">PHONE NUMBER</label>
        <div className="settings-input-wrap settings-input-wrap--icon">
          <PhoneIcon className="settings-input-icon" />
          <input
            className="settings-input"
            type="text"
            placeholder="+1 (555) 123-4567"
            value={memberUpdateInput.memberPhone ?? ""}
            onChange={memberPhoneHandler}
            name="memberPhone"
          />
        </div>
      </div>

      <div className="settings-field">
        <label className="settings-label">ADDRESS</label>
        <input
          className="settings-input"
          type="text"
          placeholder="Enter your full address"
          value={memberUpdateInput.memberAdress ?? ""}
          onChange={memberAdressHandler}
          name="memberAddress"
        />
      </div>

      <div className="settings-field">
        <label className="settings-label">DESCRIPTION</label>
        <div className="settings-textarea-wrap">
          <textarea
            className="settings-textarea"
            placeholder="Tell us a bit about yourself..."
            value={memberUpdateInput.memberDesc ?? ""}
            onChange={memberDescriptionHandler}
            name="memberDesc"
            rows={4}
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
          <span className="settings-char-count">
            {descLength} / {DESCRIPTION_MAX_LENGTH}
          </span>
        </div>
      </div>

      <div className="settings-actions">
        {onClose && (
          <button type="button" className="settings-btn settings-btn--cancel" onClick={onClose}>
            Cancel
          </button>
        )}
        <button type="button" className="settings-btn settings-btn--primary" onClick={handleSubmitButton}>
          Save Changes
        </button>
      </div>
    </Box>
  );
}
